import { Typography } from '@mui/material'
import { ExportConfigStep2 } from 'src/components/common/export-component/types'
import { DefaultErrorState } from 'src/lib/types/discounts'
import { isValidDate, isValidEndDate } from 'src/lib/utils/helpers'
import { errorMappings } from 'src/lib/utils/validations/discountsValidations'
import { defaultErrorState } from 'src/assets/DiscountsConfig'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import TextFieldError from 'src/components/common/form-elements/TextFieldError'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { useCallback, useEffect, useRef, useState } from 'react'
import TextField from '@mui/material/TextField'
import { addDays, differenceInMonths, isValid, format } from 'date-fns'

export type NewExportFormErrorState = {
  emails: DefaultErrorState[]
  email?: DefaultErrorState
  startDate: DefaultErrorState
  endDate: DefaultErrorState
  reportName: DefaultErrorState
}

const initialFormErrorState = {
  startDate: {
    status: false,
    errorCode: ''
  },
  endDate: {
    status: false,
    errorCode: ''
  }
}

export default function ExportReportOnce({
  onValidationChange,
  config,
  onChange
}: {
  onValidationChange: (isValid: boolean) => void
  config: ExportConfigStep2
  onChange: (config: ExportConfigStep2) => void
}) {
  const isFirstRender = useRef(true)

  const [startDate, setStartDate] = useState<Date>(
    config.dateRange.startDate ? new Date(config.dateRange.startDate) : addDays(new Date(), -1)
  )
  const [endDate, setEndDate] = useState<Date>(
    config.dateRange.endDate ? new Date(config.dateRange.endDate) : addDays(new Date(), -1)
  )
  const [formErrors, setFormErrors] = useState(initialFormErrorState)

  const minDate = addDays(new Date(), -90)
  const maxDate = addDays(new Date(), -1)
  const isValidForm = useCallback(() => {
    //Input Field: Start Date
    if (!isValidDate(startDate)) {
      setFormErrors(oldValues => ({
        ...oldValues,
        ...{ startDate: { status: true, errorCode: 'DC_FORM_017' } }
      }))
      return
    }
    if (startDate.setHours(0, 0, 0, 0) < minDate.setHours(0, 0, 0, 0)) {
      setFormErrors(oldValues => ({
        ...oldValues,
        ...{ startDate: { status: true, errorCode: 'DC_FORM_017' } }
      }))
      return
    }

    if (!isValidDate(endDate)) {
      setFormErrors(oldValues => ({
        ...oldValues,
        ...{ endDate: { status: true, errorCode: 'DC_FORM_018' } }
      }))
      return
    }

    if (endDate.setHours(0, 0, 0, 0) < minDate.setHours(0, 0, 0, 0)) {
      setFormErrors(oldValues => ({
        ...oldValues,
        ...{ endDate: { status: true, errorCode: 'DC_FORM_017' } }
      }))
      return
    }

    //Validation for End Date > Start Date
    if (isValidDate(endDate) && isValidDate(startDate)) {
      if (!isValidEndDate(endDate, startDate, true)) {
        setFormErrors(oldValues => ({
          ...oldValues,
          ...{ endDate: { status: true, errorCode: 'DC_FORM_040' } }
        }))
        return
      } else if (endDate && startDate && differenceInMonths(endDate, startDate) >= 3) {
        setFormErrors(oldValues => ({
          ...oldValues,
          ...{ endDate: { status: true, errorCode: 'DC_FORM_039' } }
        }))
        return
      }
    }

    setFormErrors(initialFormErrorState)
  }, [startDate, endDate])

  useEffect(() => {
    if (formErrors.startDate.status || formErrors.endDate.status) {
      onValidationChange(false)
    } else {
      onValidationChange(true)
    }
  }, [formErrors, onValidationChange])

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }

    isValidForm()
  }, [startDate, endDate, isValidForm])

  return (
    <>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <div
          style={{
            display: 'flex',
            alignItems: 'start',
            justifyContent: 'space-between',
            width: '75%',
            gap: 16
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <h2 className='text-base font-semibold'>Time period of data</h2>
            <DatePicker
              disableFuture
              minDate={minDate}
              maxDate={maxDate}
              value={startDate}
              onChange={newValue => {
                if (newValue && isValid(newValue)) {
                  setStartDate(newValue)
                  setFormErrors(oldValues => ({
                    ...oldValues,
                    ...{ startDate: defaultErrorState }
                  }))
                  onChange({
                    ...config,
                    dateRange: {
                      ...config.dateRange,
                      startDate: newValue?.toISOString()
                    }
                  })
                }
              }}
              inputFormat='dd/MM/yyyy'
              renderInput={params => (
                <TextField
                  {...params}
                  value={format(startDate, 'dd/MM/yyyy')}
                  inputProps={{
                    readOnly: true
                  }}
                  size={'small'}
                  sx={{
                    marginTop: '12px'
                  }}
                  error={formErrors?.startDate?.status}
                  onFocus={() => {
                    setFormErrors(oldValues => ({
                      ...oldValues,
                      ...{ startDate: defaultErrorState }
                    }))
                  }}
                  onBlur={() => {
                    if (!startDate) {
                      setFormErrors(oldValues => ({
                        ...oldValues,
                        ...{ startDate: { status: true, errorCode: 'DC_FORM_017' } }
                      }))
                    }
                  }}
                />
              )}
            />
            {formErrors?.startDate?.status ? (
              <TextFieldError
                message={errorMappings?.[formErrors?.startDate?.errorCode as keyof typeof errorMappings]}
              />
            ) : (
              <></>
            )}
          </div>
          <span className='mt-10'>to</span>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography
              variant='body2'
              sx={{
                fontWeight: 500,
                opacity: 0
              }}
            >
              {"''"}
            </Typography>
            <DatePicker
              disableFuture
              minDate={minDate}
              maxDate={maxDate}
              value={endDate}
              onChange={(newValue: any) => {
                if (newValue && isValid(newValue)) {
                  setEndDate(newValue)
                  setFormErrors(oldValues => ({
                    ...oldValues,
                    ...{ endDate: defaultErrorState }
                  }))
                  onChange({
                    ...config,
                    dateRange: {
                      ...config.dateRange,
                      endDate: newValue?.toISOString()
                    }
                  })
                }
              }}
              inputFormat='dd/MM/yyyy'
              renderInput={params => (
                <TextField
                  {...params}
                  value={format(endDate, 'dd/MM/yyyy')}
                  inputProps={{
                    readOnly: true
                  }}
                  size={'small'}
                  error={formErrors?.endDate?.status}
                  sx={{
                    marginTop: '10px'
                  }}
                  onFocus={() => {
                    setFormErrors(oldValues => ({
                      ...oldValues,
                      ...{ endDate: defaultErrorState }
                    }))
                  }}
                  onBlur={() => {
                    if (!endDate) {
                      setFormErrors(oldValues => ({
                        ...oldValues,
                        ...{ endDate: { status: true, errorCode: 'DC_FORM_017' } }
                      }))
                    }
                  }}
                />
              )}
            />
            {formErrors?.endDate?.status ? (
              <TextFieldError message={errorMappings?.[formErrors?.endDate?.errorCode as keyof typeof errorMappings]} />
            ) : (
              <></>
            )}
          </div>
        </div>
      </LocalizationProvider>
    </>
  )
}
